import { useEffect, useRef, type CSSProperties } from 'react'
import { formatShortDate, minutesToClock, weekdayName, type ISODate } from '@app/shared'

import { chipEnter } from '../../lib/motion'
import { X } from '../icons'

/**
 * THE PARSE CHIP — what the composer heard, printed back before anything is saved.
 *
 * The rule this component exists for: a phrase parser is only allowed to help if the owner can
 * SEE what it decided and UNDO it in one press, without retyping his sentence. So the chip is
 * not decoration — it is the whole permission slip. It prints the resolved values in the app's
 * own registers (a weekday + short date, 12-hour time) rather than echoing his tokens back,
 * because "friday" is not the claim being made; "Fri Sep 4" is.
 *
 * The ✕ does not delete anything. It hands the words back to the title and turns the parser off
 * for the rest of this line — the draft in the field was never rewritten in the first place
 * (that is the undo contract: nothing autosaves into a composer), so rejecting is a matter of
 * ignoring a reading, not of restoring lost text.
 *
 * `note` is for a surface that will not store part of what it just read — the board's quick-add
 * knows what "3pm" means and deliberately does not save it. Saying so on the chip is the
 * difference between a designed boundary and a silent drop.
 *
 * 13a · CAPTURE RESOLVES AS YOU TYPE. The row arrives one piece at a time, `CHIP_STAGGER_MS`
 * apart, in the order the brief names: the resolved values first, then the control that rejects
 * them, then the line that qualifies them — so you watch the composer settle on an interpretation
 * instead of finding one already there. Identical mechanic to the ⌘K palette's own chip row
 * (components/capture/CapturePalette.tsx), through the same helper.
 *
 * THE RULE IS THE ABSENCE, and it is enforced above by the early return: if nothing parsed, NO
 * CHIP APPEARS. Never a placeholder, never a greyed "no date" — the absence is the feedback, and
 * the stagger only ever spaces out values a parse actually produced.
 */
export function ParseChip({
  due,
  minutes,
  list,
  note,
  onReject,
}: {
  due: ISODate | null
  minutes: number | null
  list: string | null
  /** An honest caveat about what this surface will not keep. Rendered muted, under the values. */
  note?: string
  onReject: () => void
}) {
  const parts: string[] = []
  if (due) parts.push(`${weekdayName(due, true)} ${formatShortDate(due)}`)
  if (minutes !== null) parts.push(minutesToClock(minutes))
  if (list) parts.push(`#${list}`)
  const reading = parts.join(' · ')

  // Hooks before the early return, never after: an empty parse renders nothing, and a component
  // that called a hook only when it had something to say would break the rules of hooks the first
  // time a keystroke emptied it.
  const row = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const nodes = row.current?.querySelectorAll<HTMLElement>('[data-chip]')
    if (!nodes) return
    const runs = [...nodes].map((node, i) => chipEnter(node, i))
    return () => {
      for (const run of runs) run.cancel()
    }
    // Keyed on the READING, not on the render: typing another word that changes nothing about
    // what the parser committed to does not earn the stagger again.
  }, [reading, note])

  if (parts.length === 0) return null

  return (
    <div
      ref={row}
      className="flex flex-wrap items-center gap-x-[7px] gap-y-[3px] text-[11px] leading-none"
    >
      <span
        data-chip
        className="flex items-center gap-[5px] rounded-chip bg-fill-2 px-[7px] py-[4px] font-medium text-ink-2 opacity-0"
        data-testid="quickadd-parse"
      >
        <span aria-hidden>→</span>
        <span>{reading}</span>
      </span>
      <button
        type="button"
        data-chip
        onClick={onReject}
        aria-label="Keep the words as typed"
        title="Keep the words as typed — do not read a date out of them"
        style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
        className="mo-ink mo-press flex size-[18px] flex-none items-center justify-center rounded-button text-muted opacity-0 hover:text-ink-2"
      >
        <X size={11} />
      </button>
      {note && (
        <span data-chip className="text-[10px] leading-[1.4] text-faintest opacity-0">
          {note}
        </span>
      )}
    </div>
  )
}
